import { useMemo, useState, useEffect, useCallback, memo, useRef } from "react";
import { useSelector } from "react-redux";
import { Icon } from "./icon";
import { GuiSelect } from "./global-ui/gui-select";
import { GuiButton } from "./global-ui/gui-button";
import { showToast } from "./toast";
import actions from "../actions/actions";
import { dispatch, appState } from "../redux/redux-config";
import {
  STATUS_TABS_EMAIL,
  STATUS_TABS_SMS,
  STATUS_TABS_VOICE,
  STATUS_TO_CODE_EMAIL,
  STATUS_TO_CODE_SMS,
  STATUS_TO_CODE_VOICE,
  AlertType,
  Path,
  UserRole,
} from "../utilities/constants";
import navigate from "../utilities/navigation";
import "../styles/recipients-table.scss";
import { DISCARD_ROUTES } from "./../utilities/constants";
import { CreateGroupDialog } from "./create-group-dialog";

const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phone;
};

export default function RecipientsTable({
  alert,
  analyticsActions,
  hideSegmentActions,
  onSummaryLoaded,
  voiceTabStatus,
  onVoiceTabChange,
  smsTabStatus,
  onSmsTabChange,
  onSmsSummaryLoaded,
  emailTabStatus,
  onEmailTabChange,
  onEmailSummaryLoaded,
}) {
  const groups = useMemo(() => alert?.groups ?? [], [alert]);
  const isSms = alert?.alertType === AlertType.Text;
  const isVoice = alert?.alertType === AlertType.Voice;
  const isEmail = alert?.alertType === AlertType.Email;

  const channelLabel = useMemo(() => {
    const labels = [];
    const hasAnyExplicit =
      alert?.includeText ||
      alert?.includeEmail ||
      alert?.includeVoice ||
      alert?.includeVoiceIVR;
    if (hasAnyExplicit) {
      if (alert?.includeText) labels.push("Text");
      if (alert?.includeEmail) labels.push("Email");
      if (alert?.includeVoice || alert?.includeVoiceIVR) labels.push("Voice");
    } else {
      switch (alert?.alertType) {
        case AlertType.Text:
          labels.push("Text");
          break;
        case AlertType.Email:
          labels.push("Email");
          break;
        case AlertType.Voice:
          labels.push("Voice");
          break;
        default:
          labels.push("Message");
          break;
      }
    }
    return labels.join(" / ");
  }, [
    alert?.alertType,
    alert?.includeText,
    alert?.includeEmail,
    alert?.includeVoice,
    alert?.includeVoiceIVR,
  ]);
  const statusTabs = isVoice
    ? STATUS_TABS_VOICE
    : isSms
      ? STATUS_TABS_SMS
      : STATUS_TABS_EMAIL;
  const statusToCode = isVoice
    ? STATUS_TO_CODE_VOICE
    : isSms
      ? STATUS_TO_CODE_SMS
      : STATUS_TO_CODE_EMAIL;

  const segmentStatusOptions = useMemo(
    () =>
      statusTabs
        .filter((t) => t.key !== "All")
        .map((t) => ({ value: t.key, label: t.label })),
    [statusTabs],
  );

  const regularGroups = useMemo(() => {
    const regular = [];
    for (const g of groups) {
      if (g?.is_temp !== 1) regular.push(g);
    }
    return regular;
  }, [groups]);
  const [searchName, setSearchName] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [selectedTab, setSelectedTab] = useState("All");
  const [pageSize, setPageSize] = useState(5);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterGroupId, setFilterGroupId] = useState("");
  const [loading, setLoading] = useState(false);
  const [remoteRows, setRemoteRows] = useState([]);
  const [remoteTotal, setRemoteTotal] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncKey, setSyncKey] = useState(0);
  const [segmentStatus, setSegmentStatus] = useState("");
  const [segmentNoRecipients, setSegmentNoRecipients] = useState(false);
  const [creatingMessage, setCreatingMessage] = useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const syncingRef = useRef(false);
  const prevRowsRef = useRef([]);
  const prevTotalRef = useRef(0);

  const handleSync = useCallback(() => {
    prevRowsRef.current = remoteRows;
    prevTotalRef.current = remoteTotal;
    syncingRef.current = true;
    setSyncing(true);
    setSyncKey((k) => k + 1);
  }, [remoteRows, remoteTotal]);

  const isMobile = useSelector((store) => store.page.isMobile);
  const currentUser = useSelector((store) => store.user);
  const isAdmin = (currentUser?.userType ?? 0) >= UserRole.Admin;

  const groupOptions = useMemo(
    () =>
      regularGroups.map((g) => ({
        value: g?.id ?? g?.groupId,
        label: g?.name || "Group",
      })),
    [regularGroups],
  );

  const pageSizeOptions = [
    { value: 5, label: "5" },
    { value: 10, label: "10" },
    { value: 20, label: "20" },
  ];

  const startIndex = (page - 1) * pageSize;
  const total = remoteTotal || remoteRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const endIndex = Math.min(page * pageSize, total);
  const startDisplay = total === 0 ? 0 : startIndex + 1;
  // For mobile view, ensure we only show records that match the pagination count
  const displayCount = endIndex - startIndex;
  const pageRows = remoteRows.slice(0, displayCount);

  const toggleSelectAll = useCallback(
    (checked) => {
      if (checked) {
        const ids = new Set(pageRows.map((r) => r.id));
        setSelectedIds(ids);
      } else {
        setSelectedIds(new Set());
      }
    },
    [pageRows],
  );

  const toggleRow = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleNameClick = useCallback((row) => {
    if (!row.userId) return;
    const nameParts = row.name.trim().split(" ");
    localStorage.setItem(
      "selectedPerson",
      JSON.stringify({
        id: row.userId,
        name: nameParts[0] || row.name,
        userTypeId: null,
      }),
    );
    navigate(Path.PeopleDashboard.replace(":id", row.userId));
  }, []);

  const clearFilters = useCallback(() => {
    setSearchName("");
    setSearchEmail("");
    setSelectedTab("All");
    setPage(1);
    setSelectedGroupId("");
    setFilterName("");
    setFilterEmail("");
    setFilterGroupId("");
    setRemoteTotal(0);
    setLoading(false);
  }, []);

  // Sync voiceTabStatus (set by summary tiles) back to the tab bar highlight
  useEffect(() => {
    if (!isVoice) return;
    const match = statusTabs.find(
      (t) => statusToCode[t.key] === voiceTabStatus,
    );
    setSelectedTab(match ? match.key : "All");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceTabStatus, isVoice]);

  // Sync smsTabStatus (set by summary tiles) back to the tab bar highlight
  useEffect(() => {
    if (!isSms) return;
    const match = statusTabs.find((t) => statusToCode[t.key] === smsTabStatus);
    setSelectedTab(match ? match.key : "All");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smsTabStatus, isSms]);
  // Sync emailTabStatus (set by summary tiles) back to the tab bar highlight
  useEffect(() => {
    if (!isEmail) return;
    const match = statusTabs.find(
      (t) => statusToCode[t.key] === emailTabStatus,
    );
    setSelectedTab(match ? match.key : "All");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailTabStatus, isEmail]);

  async function handleCreateMessage() {
    setSegmentNoRecipients(false);
    setCreatingMessage(true);
    try {
      const alertId = alert?.id ?? alert?.alertId;
      const req = {
        alertId,
        tabStatus: isVoice
          ? (voiceTabStatus ?? 0)
          : isSms
            ? (smsTabStatus ?? 0)
            : isEmail
              ? (emailTabStatus ?? 0)
              : (statusToCode[selectedTab] ?? 0),
        groupIDFilter: filterGroupId ? Number(filterGroupId) : 0,
        lastNameFilter: filterName || "",
        emailAddressFilter: filterEmail || "",
        phoneNumberFilter: filterEmail || "",
        pageNum: 1,
        pageSize: 99999,
      };

    const allRes = await (isVoice
      ? (analyticsActions?.getMessagePreviewVoiceRecipientList ?? actions.alerts.getMessagePreviewVoiceRecipientList)(req)
      : isSms
        ? (analyticsActions?.getMessagePreviewSmsRecipientList ?? actions.alerts.getMessagePreviewSmsRecipientList)(req)
        : (analyticsActions?.getMessagePreviewEmailRecipientList ?? actions.alerts.getMessagePreviewEmailRecipientList)(req));

      if (!allRes?.success) {
        showToast("error", "Failed to load recipients. Please try again.");
        setCreatingMessage(false);
        return;
      }

      const items =
        allRes.RecipientList ||
        allRes.recipientList ||
        allRes.recipients ||
        allRes.Recipients ||
        allRes.items ||
        [];

      const userIds = items
        .map((r) => r.user_id ?? r.userId ?? r.UserId)
        .filter((id) => id != null)
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));

      if (!userIds.length) {
        setSegmentNoRecipients(true);
        setCreatingMessage(false);
        return;
      }

      const selectedLabel =
        segmentStatusOptions.find((o) => o.value === segmentStatus)?.label ||
        segmentStatus;
      const groupName = `${channelLabel} - ${selectedLabel} Segment`;

      const groupData = {
        id: 0,
        name: groupName,
        description: groupName,
        groupCode: "",
        groupType: 5,
        allowOptIn: true,
        locationId: 0,
        alwaysAttach: 0,
        callerId: "",
        emergencyCallerId: "",
        groupEmail: "",
        groupEmailName: "",
        reportingEmail: "",
        reportingEmailName: "",
        logoUrl: "",
        users: [],
        is_temp: 1,
      };

      const result = await actions.groups.save(groupData);
      if (result?.success) {
        await actions.people.addPeopleToGroup(result.id, userIds);
        // Omit is_temp from the Redux group object so SendAlert shows it
        // as a named group in the Group List, not as individual members
        const group = {
          id: result.id,
          name: groupName,
          location: 0,
          description: groupName,
          groupType: 5,
          groupTypeName: "",
          groupCode: "",
          createdUserId: 0,
          userCount: userIds.length,
          alertCount: 0,
        };
        actions.alerts.showCreateAlert(
          false,
          [group],
          null,
          DISCARD_ROUTES.messages,
        );
        // Pre-select the message type matching the originating channel
        const alertType = isSms
          ? AlertType.Text
          : isEmail
            ? AlertType.Email
            : isVoice
              ? AlertType.Voice
              : null;
        if (alertType !== null) {
          dispatch("UPDATE_NEW_ALERT", { ...appState().alert, alertType });
        }
      } else {
        showToast("error", "Failed to create segment group. Please try again.");
        setCreatingMessage(false);
      }
    } catch {
      showToast("error", "An unexpected error occurred. Please try again.");
      setCreatingMessage(false);
    }
  }

  function handleSegmentStatusChange(name, item) {
    const key = item?.value || "All";
    setSegmentStatus(item?.value ?? "");
    setSegmentNoRecipients(false);
    setLoading(true);
    setRemoteRows([]);
    setRemoteTotal(0);
    setSelectedTab(key);
    setPage(1);
    if (isVoice && onVoiceTabChange) onVoiceTabChange(statusToCode[key] ?? 0);
    if (isSms && onSmsTabChange) onSmsTabChange(statusToCode[key] ?? 0);
    if (isEmail && onEmailTabChange) onEmailTabChange(statusToCode[key] ?? 0);
  }

  const applySearch = useCallback(() => {
    setFilterName(searchName.trim());
    if (isSms || isVoice) {
      const cleanedPhone = searchEmail.replace(/\D/g, "");
      setFilterEmail(cleanedPhone);
    } else {
      setFilterEmail(searchEmail.trim());
    }
    setFilterGroupId(selectedGroupId || "");
    setPage(1);
  }, [searchName, searchEmail, selectedGroupId, isSms, isVoice]);

  useEffect(() => {
    const alertId = alert?.id ?? alert?.alertId;
    if (!alertId) {
      return;
    }

    const req = {
      alertId: alertId,
      tabStatus: isVoice
        ? (voiceTabStatus ?? 0)
        : isSms
          ? (smsTabStatus ?? 0)
          : isEmail
            ? (emailTabStatus ?? 0)
            : (statusToCode[selectedTab] ?? 0),
      groupIDFilter: filterGroupId ? Number(filterGroupId) : 0,
      lastNameFilter: filterName || "",
      emailAddressFilter: filterEmail || "",
      phoneNumberFilter: filterEmail || "",
      pageNum: page,
      pageSize: pageSize,
    };

    let cancelled = false;
    setLoading(true);

    const action = isVoice
      ? (analyticsActions?.getMessagePreviewVoiceRecipientList ?? actions.alerts.getMessagePreviewVoiceRecipientList)(req)
      : isSms
        ? (analyticsActions?.getMessagePreviewSmsRecipientList ?? actions.alerts.getMessagePreviewSmsRecipientList)(req)
        : (analyticsActions?.getMessagePreviewEmailRecipientList ?? actions.alerts.getMessagePreviewEmailRecipientList)(req);

    action
      .then((res) => {
        if (cancelled) return;
        if (res && res.success) {
          const items =
            res.RecipientList ||
            res.recipientList ||
            res.recipients ||
            res.Recipients ||
            res.items ||
            [];
          const rows = items.map((r, idx) => {
            const first = r.first_name ?? r.firstName ?? r.FirstName ?? "";
            const last = r.last_name ?? r.lastName ?? r.LastName ?? "";
            const name = (r.name ?? r.Name ?? `${first} ${last}`).trim();
            const email = r.email_address ?? r.email ?? r.Email ?? "-";
            const status = r.Status ?? r.status ?? selectedTab;
            const summary = res.statusSummary;
            if (isVoice && onSummaryLoaded && summary && isInitialLoad) {
              onSummaryLoaded(summary);
              setIsInitialLoad(false);
            }
            if (isSms && onSmsSummaryLoaded && summary && isInitialLoad) {
              onSmsSummaryLoaded(summary);
              setIsInitialLoad(false);
            }
            if (isEmail && onEmailSummaryLoaded && summary && isInitialLoad) {
              onEmailSummaryLoaded(summary);
              setIsInitialLoad(false);
            }

            if (isVoice) {
              return {
                id: `${r.user_id ?? ""}-${r.number_dialed ?? ""}-${idx}`,
                userId: r.user_id ?? null,
                name,
                phoneNumber: r.number_dialed,
                status: r.call_answered ? "Opened" : "Unopened",
                answeredBy: r.machine_detection || "-",
                startTime: (() => {
                  if (!r.start_time) return "-";
                  const d = new Date(r.start_time);
                  const month = d.getMonth() + 1;
                  const day = d.getDate();
                  const year = d.getFullYear();
                  let hours = d.getHours();
                  const minutes = String(d.getMinutes()).padStart(2, "0");
                  const ampm = hours >= 12 ? "PM" : "AM";
                  hours = hours % 12 || 12;
                  return `${month}/${day}/${year} ${hours}:${minutes}${ampm}`;
                })(),
                duration: r.duration ?? "-",
                profilePhotoUrl: r.profilePhotoURL ?? null,
              };
            }

            if (isSms) {
              return {
                id: `${r.user_id ?? ""}-${r.number_dialed ?? ""}-${idx}`,
                userId: r.user_id ?? null,
                name,
                phoneNumber: r.number_dialed,
                deliveryStatus: r.status,
                reason: r.reason,
                optOutActivity: r.opted_out,
                profilePhotoUrl: r.profilePhotoURL ?? null,
              };
            }

            let openedOn = r.Opened ?? null;
            if (openedOn) {
              try {
                const d = new Date(openedOn);
                openedOn = isNaN(d.getTime())
                  ? String(openedOn)
                  : d.toLocaleString();
              } catch (e) {
                openedOn = String(openedOn);
              }
            } else {
              openedOn = "-";
            }
            const clickedThru = r.UrlClicked ?? r.UrlClicked ?? "-";
            const spamReported = r.SpamReported ?? r.SpamReported ?? "-";
            const unsubscribed = r.Unsubscribed ?? r.Unsubscribed ?? "-";
            const profilePhotoUrl =
              r.profilePhotoURL ?? r.profilePhotoURL ?? null;

            return {
              id:
                (r.email_address ?? r.EmailAddress ?? email ?? idx) +
                "-" +
                name +
                "-" +
                idx,
              userId: r.user_id ?? r.UserId ?? null,
              name,
              email,
              status,
              openedOn,
              clickedThru,
              spamReported,
              unsubscribed,
              profilePhotoUrl,
            };
          });

          // Set total count for pagination from first item's count if available
          if (items.length > 0 && typeof items[0].count === "number") {
            setRemoteTotal(items[0].count);
          } else {
            setRemoteTotal(items.length);
          }
          setRemoteRows(rows);
          if (syncingRef.current) {
            showToast("success", "Recipients synced successfully.");
            syncingRef.current = false;
            setSyncing(false);
          }
        }
      })
      .catch(() => {
        if (!cancelled && syncingRef.current) {
          showToast("error", "Sync failed. Please try again.");
          setRemoteRows(prevRowsRef.current);
          setRemoteTotal(prevTotalRef.current);
          syncingRef.current = false;
          setSyncing(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    alert,
    isSms,
    isVoice,
    selectedTab,
    statusToCode,
    filterName,
    filterEmail,
    filterGroupId,
    page,
    pageSize,
    voiceTabStatus,
    smsTabStatus,
    emailTabStatus,
    onSummaryLoaded,
    onSmsSummaryLoaded,
    onEmailSummaryLoaded,
    isInitialLoad,
    syncKey,
    isEmail,
  ]);

  const csvEscape = (v) => {
    const s = v == null ? "" : String(v);
    const needsQuote = /[",\n]/.test(s);
    const esc = s.replace(/"/g, '""');
    return needsQuote ? `"${esc}"` : esc;
  };

  const exportSelection = useCallback(() => {
    const selected = remoteRows.filter((r) => selectedIds.has(r.id));
    if (selected.length === 0) return;

    let headers;
    let lines;

    if (isVoice) {
      headers = [
        "Recipient",
        "Phone Number",
        "Status",
        "Answered By",
        "Start Time",
        "Duration",
      ];
      lines = selected.map((r) =>
        [r.name, r.phoneNumber, r.status, r.answeredBy, r.startTime, r.duration]
          .map(csvEscape)
          .join(","),
      );
    } else if (isSms) {
      headers = [
        "Recipient",
        "Phone Number",
        "Delivery Status",
        "Reason",
        "Opt-Out Activity",
      ];
      lines = selected.map((r) =>
        [r.name, r.phoneNumber, r.deliveryStatus, r.reason, r.optOutActivity]
          .map(csvEscape)
          .join(","),
      );
    } else {
      headers = [
        "Recipient",
        "Email Address",
        "Status",
        "Opened",
        "Clicked Thru",
        "Spam Reported",
        "Unsubscribed",
      ];
      lines = selected.map((r) =>
        [
          r.name,
          r.email,
          r.status,
          r.openedOn,
          r.clickedThru,
          r.spamReported,
          r.unsubscribed,
        ]
          .map(csvEscape)
          .join(","),
      );
    }

    const csv = [headers.join(","), ...lines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
    a.download = `recipients-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [remoteRows, selectedIds, isSms, isVoice]);

  const selectedRows = useMemo(
    () => remoteRows.filter((r) => selectedIds.has(r.id)),
    [remoteRows, selectedIds],
  );

  const handleSendMessage = useCallback(() => {
    if (selectedRows.length === 0) return;
    const users = selectedRows
      .filter((r) => r.userId != null)
      .map((r) => ({ id: r.userId, name: r.name }));
    if (users.length === 0) {
      showToast("error", "No valid recipients found for messaging.");
      return;
    }
    try {
      actions.alerts.sendMessageToUsers(alert, users);
    } catch {
      showToast("error", "Failed to start message flow. Please try again.");
    }
  }, [selectedRows, alert]);

  const handleCreateGroupSuccess = useCallback(() => {
    setShowCreateGroupModal(false);
    setSelectedIds(new Set());
  }, []);

  const pageButtons = useMemo(() => {
    const range = 5;
    let start = Math.max(1, currentPage - Math.floor(range / 2));
    let end = Math.min(totalPages, start + range - 1);

    // Adjust start if we're near the end
    if (end - start + 1 < range) {
      start = Math.max(1, end - range + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  const RecipientRow = memo(function RecipientRow({ row, selected, onToggle }) {
    const status = row.status ?? row.deliveryStatus;
    const statusClass = `status-pill status-${String(status).toLowerCase()}`;

    const getStatusIcon = (status) => {
      const s = String(status).toLowerCase();
      if (
        s.includes("delivered") ||
        s.includes("success") ||
        s.includes("sent")
      )
        return "fa fa-check-circle";
      if (s.includes("failed")) return "fa fa-times-circle";
      if (s.includes("pending")) return "fa fa-clock";
      if (s.includes("unopened")) return "fa fa-envelope";
      if (s.includes("opened")) return "fa fa-envelope-open";
      return null;
    };

    const statusIcon = getStatusIcon(status);

    const optOutDate = useMemo(() => {
      const val = row.optOutActivity;
      if (val == null || val === "" || val === false || val === 0) return null;
      const str = String(val).trim();
      if (!str || str === "false" || str === "0") return null;
      try {
        const d = new Date(str);
        if (!isNaN(d.getTime())) {
          return d.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "2-digit",
          });
        }
      } catch (e) {
        // not a date
      }
      return str === "true" || str === "1" ? "Yes" : str;
    }, [row.optOutActivity]);

    return (
      <tr key={row.id + row.name}>
        <td>
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggle(row.id)}
            aria-label={`Select ${row.name}`}
          />
        </td>
        <td>
          <div className="cell-primary">
            {row.profilePhotoUrl ? (
              <img
                src={row.profilePhotoUrl}
                alt={row.name}
                className="recipient-avatar"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <Icon icon="user-circle" className="recipient-avatar" />
            )}
            {isAdmin && row.userId ? (
              <span
                className="recipient-name-link"
                role="button"
                tabIndex={0}
                onClick={() => handleNameClick(row)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") handleNameClick(row);
                }}
              >
                {row.name}
              </span>
            ) : (
              row.name
            )}
          </div>
        </td>
        {isVoice ? (
          <>
            <td>{formatPhoneNumber(row.phoneNumber)}</td>
            <td>
              <span className={statusClass}>
                {statusIcon && <i className={statusIcon} aria-hidden="true" />}
                {row.status}
              </span>
            </td>
            <td>{row.answeredBy}</td>
            <td>{row.startTime}</td>
            <td>{row.duration}</td>
          </>
        ) : isSms ? (
          <>
            <td>{formatPhoneNumber(row.phoneNumber)}</td>
            <td>
              <span className={statusClass}>
                {statusIcon && <i className={statusIcon} aria-hidden="true" />}
                {status}
              </span>
            </td>
            <td>{row.reason}</td>
            <td style={{ color: optOutDate ? "red" : "inherit" }}>
              {optOutDate || "-"}
            </td>
          </>
        ) : (
          <>
            <td>{row.email}</td>
            <td>
              <span className={statusClass}>
                {statusIcon && <i className={statusIcon} aria-hidden="true" />}
                {status}
              </span>
            </td>
            <td>{row.openedOn}</td>
            <td>{row.clickedThru}</td>
            <td className="cell-danger">{row.spamReported}</td>
            <td className="cell-danger">{row.unsubscribed}</td>
          </>
        )}
      </tr>
    );
  });

  const RecipientCard = memo(function RecipientCard({
    row,
    selected,
    onToggle,
  }) {
    const status = row.status ?? row.deliveryStatus;
    const statusClass = `status-pill status-${String(status).toLowerCase()}`;

    const getStatusIcon = (status) => {
      const s = String(status).toLowerCase();
      if (
        s.includes("delivered") ||
        s.includes("success") ||
        s.includes("sent")
      )
        return "fa fa-check-circle";
      if (s.includes("failed")) return "fa fa-times-circle";
      if (s.includes("pending")) return "fa fa-clock";
      if (s.includes("unopened")) return "fa fa-envelope";
      if (s.includes("opened")) return "fa fa-envelope-open";
      return null;
    };

    const statusIcon = getStatusIcon(status);

    const optOutDate = useMemo(() => {
      const val = row.optOutActivity;
      if (val == null || val === "" || val === false || val === 0) return null;
      const str = String(val).trim();
      if (!str || str === "false" || str === "0") return null;
      try {
        const d = new Date(str);
        if (!isNaN(d.getTime())) {
          return d.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "2-digit",
          });
        }
      } catch (e) {
        // not a date
      }
      return str === "true" || str === "1" ? "Yes" : str;
    }, [row.optOutActivity]);

    return (
      <div className="recipient-card">
        <div className="card-header">
          <input
            type="checkbox"
            className="recipient-checkbox"
            checked={selected}
            onChange={() => onToggle(row.id)}
            aria-label={`Select ${row.name}`}
          />
          <div className="cell-primary">
            {row.profilePhotoUrl ? (
              <img
                src={row.profilePhotoUrl}
                alt={row.name}
                className="recipient-avatar"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <Icon icon="user-circle" className="recipient-avatar" />
            )}
            {isAdmin && row.userId ? (
              <span
                className="recipient-name recipient-name-link"
                role="button"
                tabIndex={0}
                onClick={() => handleNameClick(row)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") handleNameClick(row);
                }}
              >
                {row.name}
              </span>
            ) : (
              <span className="recipient-name">{row.name}</span>
            )}
          </div>
          <div className={`status-badge ${statusClass}`}>
            {statusIcon && <i className={statusIcon} aria-hidden="true" />}
            {status}
          </div>
        </div>
        <div className="card-body">
          {isVoice ? (
            <>
              <div className="info-row">
                <span className="info-label">Phone:</span>
                <span className="info-value">
                  {formatPhoneNumber(row.phoneNumber)}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Answered By:</span>
                <span className="info-value">{row.answeredBy}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Start Time:</span>
                <span className="info-value">{row.startTime}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Duration:</span>
                <span className="info-value">{row.duration}</span>
              </div>
            </>
          ) : isSms ? (
            <>
              <div className="info-row">
                <span className="info-label">Phone:</span>
                <span className="info-value">
                  {formatPhoneNumber(row.phoneNumber)}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Comment:</span>
                <span className="info-value">{row.reason}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Opted Out:</span>
                <span
                  className="info-value"
                  style={{ color: optOutDate ? "red" : "inherit" }}
                >
                  {optOutDate || "-"}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="info-row">
                <span className="info-label">Email:</span>
                <span className="info-value">{row.email}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Opened:</span>
                <span className="info-value">{row.openedOn}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Clicked:</span>
                <span className="info-value">{row.clickedThru}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Spam:</span>
                <span className="info-value cell-danger">
                  {row.spamReported}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Unsubscribed:</span>
                <span className="info-value cell-danger">
                  {row.unsubscribed}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  });

  return (
    <div className="recipients-container">
      {/* Toolbar */}
      <div className="recipients-toolbar">
        <div className="recipients-filters">
          <div className="filter-input">
            <Icon icon="search" className="fas" />
            <input
              type="text"
              placeholder="Search by last name"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
          </div>
          <div className="filter-input">
            <Icon icon="search" className="fas" />
            <input
              type="text"
              placeholder={`Search by ${isSms || isVoice ? "phone number" : "email address"}`}
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
            />
          </div>
          <div className="filter-select">
            <GuiSelect
              className="group-filter-select"
              value={selectedGroupId}
              onChange={(name, item) => setSelectedGroupId(item?.value ?? "")}
              options={groupOptions}
              placeholder="Select Group"
              isClearable
              disabled={regularGroups.length === 0}
            />
          </div>
          <div className="filters-actions">
            <button className="btn btn-outline" onClick={applySearch}>
              Search
            </button>
            <button className="btn btn-link" onClick={clearFilters}>
              Clear
            </button>
          </div>
        </div>
        <div className="recipients-actions">
          <button
            className="btn btn-outline"
            onClick={handleSync}
            disabled={syncing || loading}
            aria-label="Sync recipients"
          >
            <Icon
              icon={syncing ? "spinner" : "sync"}
              className={syncing ? "fas fa-spin" : "fas"}
            />{" "}
            {syncing ? "Syncing..." : "Sync"}
          </button>
          <button
            className="btn btn-secondary"
            onClick={exportSelection}
            disabled={selectedIds.size === 0}
          >
            <Icon icon="download" className="fas" /> Export Selection
          </button>
        </div>
      </div>

      {/* Selection Action Bar */}
      <div className="recipients-selection-bar">
        <span className="selection-count">
          {selectedIds.size > 0 ? `${selectedIds.size} selected` : "0 selected"}
        </span>
        <div className="selection-divider" />
        <div className="selection-actions">
          {!hideSegmentActions && (
            <button
              className="btn btn-selection-action"
              onClick={handleSendMessage}
              disabled={selectedIds.size === 0}
            >
              <Icon icon="paper-plane" className="fas" />
              <span>Send Message</span>
            </button>
          )}
          {!hideSegmentActions && (
            <button
              className="btn btn-selection-action"
              onClick={() => setShowCreateGroupModal(true)}
              disabled={selectedIds.size === 0}
            >
              <Icon icon="users" className="fas" />
              <span>Create Group</span>
            </button>
          )}
        </div>
      </div>

      {/* Create Group Modal */}
      {showCreateGroupModal && (
        <CreateGroupDialog
          selectedRows={selectedRows}
          onClose={() => setShowCreateGroupModal(false)}
          onSuccess={handleCreateGroupSuccess}
        />
      )}

      {/* Tabs */}
      <div className="recipients-tabs" role="tablist">
        {statusTabs.map((t) => (
          <button
            key={t.key}
            className={"tab" + (selectedTab === t.key ? " active" : "")}
            role="tab"
            aria-selected={selectedTab === t.key}
            onClick={() => {
              // Show loader immediately when switching tabs
              setLoading(true);
              setRemoteRows([]);
              setRemoteTotal(0);
              setSelectedTab(t.key);
              setPage(1);
              if (isVoice && onVoiceTabChange) {
                onVoiceTabChange(statusToCode[t.key] ?? 0);
              }
              if (isSms && onSmsTabChange) {
                onSmsTabChange(statusToCode[t.key] ?? 0);
              }
              if (isEmail && onEmailTabChange) {
                onEmailTabChange(statusToCode[t.key] ?? 0);
              }
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Table */}
      {isMobile ? (
        <div className="recipients-cards-wrapper">
          {!loading && pageRows.length > 0 && (
            <div className="cards-select-all-bar">
              <label className="cards-select-all-label">
                <input
                  type="checkbox"
                  aria-label="Select all visible rows"
                  checked={
                    pageRows.length > 0 &&
                    pageRows.every((r) => selectedIds.has(r.id))
                  }
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                />
                <span>Select All</span>
              </label>
              {selectedIds.size > 0 && (
                <span className="cards-selected-count">
                  {selectedIds.size} selected
                </span>
              )}
            </div>
          )}
          {loading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <i className="fas fa-spinner fa-spin" /> Loading...
            </div>
          ) : pageRows.length === 0 ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              No recipients found.
            </div>
          ) : (
            pageRows.map((row) => (
              <RecipientCard
                key={row.id}
                row={row}
                selected={selectedIds.has(row.id)}
                onToggle={toggleRow}
              />
            ))
          )}
        </div>
      ) : (
        <div className="recipients-table-wrapper">
          <table className="recipients-table">
            <thead>
              <tr>
                <th style={{ width: "36px" }}>
                  <input
                    type="checkbox"
                    aria-label="Select page rows"
                    checked={
                      pageRows.length > 0 &&
                      pageRows.every((r) => selectedIds.has(r.id))
                    }
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                  />
                </th>
                <th>Recipient</th>
                {isVoice ? (
                  <>
                    <th>Phone Number</th>
                    <th>Status</th>
                    <th>Answered By</th>
                    <th>Start Time</th>
                    <th>Duration</th>
                  </>
                ) : isSms ? (
                  <>
                    <th>Phone Number</th>
                    <th>Status</th>
                    <th>Comment/Reason</th>
                    <th>Opted Out</th>
                  </>
                ) : (
                  <>
                    <th>Email Address</th>
                    <th>Status</th>
                    <th>Opened On</th>
                    <th>Clicked Thru</th>
                    <th>Spam Reported</th>
                    <th>Unsubscribed</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={isVoice ? 7 : isSms ? 6 : 8}
                    style={{ textAlign: "center" }}
                  >
                    <i className="fas fa-spinner fa-spin" /> Loading...
                  </td>
                </tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={isVoice ? 7 : isSms ? 6 : 8}
                    style={{ textAlign: "center" }}
                  >
                    No recipients found.
                  </td>
                </tr>
              ) : (
                pageRows.map((row) => (
                  <RecipientRow
                    key={row.id}
                    row={row}
                    selected={selectedIds.has(row.id)}
                    onToggle={toggleRow}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div
        className="recipients-pagination"
        role="navigation"
        aria-label="Recipients pagination"
      >
        <div className="pager-left">
          <button
            className="pager-btn"
            disabled={currentPage === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          >
            <Icon icon="chevron-left" className="fas" />
          </button>
          <div className="pager-pages">
            {pageButtons.map((pageNumber) => (
              <button
                key={pageNumber}
                className={
                  "pager-page" + (pageNumber === currentPage ? " active" : "")
                }
                onClick={() => setPage(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
          </div>
          <button
            className="pager-btn"
            disabled={currentPage === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label="Next page"
          >
            <Icon icon="chevron-right" className="fas" />
          </button>
        </div>
        <div className="pager-right">
          <span className="pager-show">Show:</span>
          <GuiSelect
            className="pager-size-select"
            value={pageSize}
            onChange={(name, item) => {
              setPageSize(item.value);
              setPage(1);
            }}
            options={pageSizeOptions}
          />
          <span className="pager-count">
            Showing {startDisplay}-{endIndex} of {total}
          </span>
        </div>
      </div>

      <div className="send-text-segment-label-wrapper">
        <div className="send-text-segment-card-title">
          <div className="send-text-segment-label">
            Send {channelLabel} to Segment
          </div>
        </div>
        <div className="send-text-segment-controls">
          <GuiSelect
            className="segment-status-select"
            name="segmentStatus"
            label="Segment"
            placeholder="Select Status"
            options={segmentStatusOptions}
            value={segmentStatus}
            onChange={handleSegmentStatusChange}
            enableClear
            disabled={loading || creatingMessage}
          />
          <GuiButton
            className="btn-outline"
            disabled={!segmentStatus || loading}
            loading={creatingMessage}
            onClick={handleCreateMessage}
          >
            Create Message
          </GuiButton>
        </div>
        {segmentNoRecipients && (
          <div className="segment-no-recipients">
            No recipients available for selected status.
          </div>
        )}
      </div>

      {/* Not requred as per new designs, but can be added back in if needed in the future */}
      {/* {isSms && (
        <div className="send-text-segment">
          <h3>Send Text to Segment</h3>
          <div className="segment-controls">
            <GuiSelect
              className="segment-status-select"
              value={segmentStatus}
              onChange={(name, item) => setSegmentStatus(item?.value ?? "")}
              options={segmentStatusOptions}
              placeholder="Select Status"
              isClearable
            />
            <button className="btn btn-primary">Create Message</button>
          </div>
        </div>
      )} */}
    </div>
  );
}
